import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CalendarClock, CheckCircle2, ClipboardList, MapPin, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { DatetimePicker } from '../../../components/ui/datetimePicker';
import { FieldBox } from '../../../components/ui/field-box';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import {
  CompleteModal,
  RescheduleModal,
  type SiteVisitStatus,
  StatusBadge,
  fmt,
} from './shared-visit-ui';

// ── Types ──────────────────────────────────────────────────────────────────────

type SiteVisitType = 'BEEAH_CONSULTANT' | 'LAB' | 'SM_CONSULTANT';

interface SiteVisit {
  id: string;
  visitType: SiteVisitType;
  status: SiteVisitStatus;
  name: string | null;
  address: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  notes: string | null;
  rescheduledDate: string | null;
  rescheduleRemarks: string | null;
  consultant: { id: string; name: string; email: string } | null;
  createdBy: { id: string; name: string };
}

interface GroupedVisits {
  BEEAH_CONSULTANT: SiteVisit[];
  LAB: SiteVisit[];
  SM_CONSULTANT: SiteVisit[];
}

interface CreateForm {
  name: string;
  address: string;
  visitDate: string;
  comments: string;
}

// ── Create Modal ───────────────────────────────────────────────────────────────

function CreateVisitModal({
  open,
  visitType,
  defaultAddress,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  visitType: SiteVisitType;
  defaultAddress?: string;
  onClose: () => void;
  onSubmit: (form: CreateForm) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState<CreateForm>({
    name: '',
    address: defaultAddress ?? '',
    visitDate: '',
    comments: '',
  });
  // Reset (and re-apply the request's default address) each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({ name: '', address: defaultAddress ?? '', visitDate: '', comments: '' });
    }
  }, [open, defaultAddress]);
  const set =
    (k: keyof CreateForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const LABEL: Record<SiteVisitType, string> = {
    BEEAH_CONSULTANT: 'Beeah Consultant Site Visit',
    LAB: 'Lab Site Visit',
    SM_CONSULTANT: 'SM Consultant Site Visit',
  };

  const isValid = !!form.visitDate && form.name.trim().length > 0;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Schedule ${LABEL[visitType]}`}
      description="Visit name and scheduled date & time are required — other fields can be filled in later."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sv-name">
            Visit Name <span className="text-error-500">*</span>
          </label>
          <input
            id="sv-name"
            type="text"
            value={form.name}
            onChange={set('name')}
            placeholder="e.g. Initial Site Assessment"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sv-address">
            Location / Address
          </label>
          <input
            id="sv-address"
            type="text"
            value={form.address}
            onChange={set('address')}
            placeholder="e.g. Al Majaz, Sharjah"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">
            Scheduled Date &amp; Time <span className="text-error-500">*</span>
          </label>
          <DatetimePicker
            value={form.visitDate ? new Date(form.visitDate) : undefined}
            onChange={(date) =>
              setForm((f) => ({ ...f, visitDate: date ? date.toISOString() : '' }))
            }
            granularity="minute"
            placeholder="Pick date & time"
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sv-comments">
            Comments
          </label>
          <textarea
            id="sv-comments"
            rows={3}
            value={form.comments}
            onChange={set('comments')}
            placeholder="Any notes or instructions…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => onSubmit(form)}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Scheduling…' : 'Schedule Visit'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── Beeah Consultant Visit Detail (single-visit view) ──────────────────────────

function BeeahVisitDetail({
  visit,
  canAct,
  onStart,
  onComplete,
  onReschedule,
}: {
  visit: SiteVisit | undefined;
  canAct: boolean;
  onStart: (id: string) => void;
  onComplete: (id: string) => void;
  onReschedule: () => void;
}) {
  if (!visit) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-700">Beeah Consultant Site Visit</h3>
        </div>
        <div className="px-5 py-10 text-center">
          <Calendar size={32} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-medium text-gray-500">No site visit scheduled yet</p>
          <p className="mt-1 text-xs text-gray-400">
            Use the "Schedule Site Visit" button above once a consultant is assigned.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-700">Beeah Consultant Site Visit</h3>
        <StatusBadge status={visit.status} />
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 px-5 py-5">
        <FieldBox icon={<ClipboardList size={16} />} label="Visit Name">
          {visit.name ?? '—'}
        </FieldBox>
        <FieldBox icon={<MapPin size={16} />} label="Location / Address">
          {visit.address ?? '—'}
        </FieldBox>
        <FieldBox icon={<CalendarClock size={16} />} label="Scheduled Date & Time">
          {fmt(visit.scheduledAt) ?? '—'}
        </FieldBox>
        <FieldBox icon={<User size={16} />} label="Consultant">
          {visit.consultant?.name ?? '—'}
        </FieldBox>
        {visit.completedAt && (
          <FieldBox icon={<CheckCircle2 size={16} />} label="Completed At">
            {fmt(visit.completedAt)}
          </FieldBox>
        )}
        {visit.notes && (
          <FieldBox icon={<ClipboardList size={16} />} label="Notes">
            {visit.notes}
          </FieldBox>
        )}
      </div>
      {canAct && visit.status !== 'COMPLETED' && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 px-5 py-4">
          {(visit.status === 'NOT_STARTED' || visit.status === 'APPROVED') && (
            <Button variant="outline" onClick={() => onStart(visit.id)}>
              Mark In Progress
            </Button>
          )}
          {visit.status === 'IN_PROGRESS' && (
            <Button
              variant="primary"
              className="bg-success-500 hover:bg-success-600"
              onClick={() => onComplete(visit.id)}
            >
              Mark Completed
            </Button>
          )}
          <Button variant="outline" onClick={onReschedule}>
            Reschedule
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function SiteVisitsTab({
  requestId,
  defaultAddress,
  autoOpenTrigger,
  onMutated,
}: {
  requestId: string;
  defaultAddress?: string;
  autoOpenTrigger?: number;
  onMutated?: () => void;
}) {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canAct = can('sm-site-visit:create');

  const [createOpen, setCreateOpen] = useState(false);
  const [createType] = useState<SiteVisitType>('BEEAH_CONSULTANT');
  const [completeVisitId, setCompleteVisitId] = useState<string | null>(null);
  const [rescheduleVisitId, setRescheduleVisitId] = useState<string | null>(null);

  useEffect(() => {
    if (autoOpenTrigger) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTrigger]);

  const visitsKey = ['site-visits', requestId];

  const { data, isLoading, isError } = useQuery({
    queryKey: visitsKey,
    queryFn: () => api.get(`study-requests/${requestId}/site-visits`).json<GroupedVisits>(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: visitsKey });
    onMutated?.();
  };

  const createMutation = useMutation({
    mutationFn: (form: CreateForm & { visitType: SiteVisitType }) =>
      api
        .post(`study-requests/${requestId}/site-visits`, {
          json: {
            visitType: form.visitType,
            name: form.name || undefined,
            address: form.address || undefined,
            visitDate: form.visitDate || undefined,
            comments: form.comments || undefined,
          },
        })
        .json(),
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });

  const startMutation = useMutation({
    mutationFn: (visitId: string) =>
      api.patch(`study-requests/${requestId}/site-visits/${visitId}/start`).json(),
    onSuccess: invalidate,
  });

  const completeMutation = useMutation({
    mutationFn: ({ visitId, comments }: { visitId: string; comments: string }) =>
      api
        .patch(`study-requests/${requestId}/site-visits/${visitId}/complete`, {
          json: comments ? { comments } : {},
        })
        .json(),
    onSuccess: () => {
      setCompleteVisitId(null);
      invalidate();
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({
      visitId,
      scheduledAt,
      reason,
    }: {
      visitId: string;
      scheduledAt: string;
      reason: string;
    }) =>
      api
        .patch(`study-requests/${requestId}/site-visits/${visitId}/reschedule`, {
          json: { scheduledAt, reason },
        })
        .json(),
    onSuccess: () => {
      setRescheduleVisitId(null);
      invalidate();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-2xl bg-gray-100" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
        <p className="text-sm font-medium text-error-600">Failed to load site visits</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BeeahVisitDetail
        visit={data.BEEAH_CONSULTANT[0]}
        canAct={canAct}
        onStart={(id) => startMutation.mutate(id)}
        onComplete={(id) => setCompleteVisitId(id)}
        onReschedule={() => setRescheduleVisitId(data.BEEAH_CONSULTANT[0]?.id ?? null)}
      />

      <CreateVisitModal
        open={createOpen}
        visitType={createType}
        defaultAddress={defaultAddress}
        onClose={() => setCreateOpen(false)}
        onSubmit={(form) => createMutation.mutate({ ...form, visitType: createType })}
        isPending={createMutation.isPending}
      />

      <CompleteModal
        open={!!completeVisitId}
        onClose={() => setCompleteVisitId(null)}
        onSubmit={(comments) =>
          completeVisitId && completeMutation.mutate({ visitId: completeVisitId, comments })
        }
        isPending={completeMutation.isPending}
      />

      <RescheduleModal
        open={!!rescheduleVisitId}
        currentScheduledAt={
          data.BEEAH_CONSULTANT.find((v) => v.id === rescheduleVisitId)?.scheduledAt ?? null
        }
        onClose={() => setRescheduleVisitId(null)}
        onSubmit={(form) =>
          rescheduleVisitId && rescheduleMutation.mutate({ visitId: rescheduleVisitId, ...form })
        }
        isPending={rescheduleMutation.isPending}
      />
    </div>
  );
}
